/**
 * Generic Entity Sharing Service
 * API client for sharing data bundles and skills
 */

import api from '@services/api';
import type {
  EntityShare,
  ShareEntityPayload,
  EntitySharesListResponse,
  EntityShareResponse,
  ShareableUser,
  ShareableTenant,
  BulkSharePayload,
  BulkShareResult,
  BulkShareUsersPayload,
  BulkShareUsersResult,
} from '@/types';

// `flows/courses` is multi-segment intentionally — it makes the existing
// generic URL builders (`/${entityType}/${id}/share` etc.) resolve to
// `/flows/courses/<id>/share`, which matches the actual backend route.
// `getSharedEntities` has its own route to avoid the `/courses/<id>`
// converter collision; courses use a dedicated `getSharedCourses` helper
// in flowsService instead.
export type ShareableEntityType = 'data-bundles' | 'skills' | 'flows/courses';

export interface QuickShareInfo {
  success: boolean;
  role: string;
  own_tenant: {
    tenant_id: string;
    tenant_name: string;
    tenant_code: string;
  };
  child_tenants: Array<{
    tenant_id: string;
    tenant_name: string;
    tenant_code: string;
  }>;
}

export const sharingService = {
  shareEntity: async (entityType: ShareableEntityType, entityId: string, data: ShareEntityPayload): Promise<EntityShare> => {
    const response = await api.post<EntityShareResponse>(`/${entityType}/${entityId}/share`, data);
    return response.data.share;
  },

  listEntityShares: async (entityType: ShareableEntityType, entityId: string): Promise<EntityShare[]> => {
    const response = await api.get<EntitySharesListResponse>(`/${entityType}/${entityId}/shares`);
    return response.data.shares;
  },

  removeEntityShare: async (entityType: ShareableEntityType, entityId: string, shareId: string): Promise<void> => {
    await api.delete(`/${entityType}/${entityId}/shares/${shareId}`);
  },

  bulkShareEntity: async (entityType: ShareableEntityType, entityId: string, data: BulkSharePayload): Promise<BulkShareResult> => {
    const response = await api.post<BulkShareResult>(`/${entityType}/${entityId}/bulk-share`, data);
    return response.data;
  },

  bulkShareEntityUsers: async (entityType: ShareableEntityType, entityId: string, data: BulkShareUsersPayload): Promise<BulkShareUsersResult> => {
    const response = await api.post<BulkShareUsersResult>(`/${entityType}/${entityId}/bulk-share-users`, data);
    return response.data;
  },

  getShareTargets: async (): Promise<{ users: ShareableUser[]; tenants: ShareableTenant[] }> => {
    const response = await api.get<{ success: boolean; users: ShareableUser[]; tenants: ShareableTenant[] }>('/group-admin/share-targets');
    return { users: response.data.users, tenants: response.data.tenants };
  },

  getQuickShareInfo: async (): Promise<QuickShareInfo> => {
    const response = await api.get<QuickShareInfo>('/group-admin/quick-share-info');
    return response.data;
  },

  getSharedEntities: async (entityType: ShareableEntityType): Promise<any[]> => {
    const key = entityType === 'data-bundles' ? 'data_bundles' : 'skills';
    const response = await api.get<{ success: boolean; [key: string]: any }>(`/${entityType}/shared`);
    return response.data[key];
  },
};
